import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import FarmersNav from "@/components/FarmersNav";
import { renderBlock, SUPPORTED_BLOCK_KINDS } from "@/components/farmer_site/BlockRenderer";
import { themeVars } from "@/lib/farmer_site_themes";
import { FarmerSiteHeader, FarmerSiteFooter } from "@/components/farmer_site/FarmerSiteChrome";
import {
  ArrowLeft, ArrowUp, ArrowDown, Trash, Plus, Palette, Eye, EyeSlash,
  Rocket, Storefront, PencilSimple, X, Check, Globe, Copy, Sparkle,
  Link as LinkIcon, Warning, Basket, ChatCircle, Camera, MagicWand,
} from "@phosphor-icons/react";
/**
 * FarmersWebsiteEditor — /farmers/websites/:id
 *
 * Left rail:  Pages list + block palette + settings
 * Right rail: WYSIWYG-ish preview using the exact same block renderer
 *              as the public site (so what you see IS what visitors see).
 */
const BLOCK_CATALOG = [
  { kind: "hero", label: "Hero", icon: Sparkle, note: "Big headline + photo" },
  { kind: "trust-badges", label: "Trust badges", icon: Check, note: "Fresh / Local / Family" },
  { kind: "fresh-today", label: "Fresh today", icon: Storefront, note: "Products picked in 72h" },
  { kind: "products", label: "Products grid", icon: Basket, note: "Shop your produce" },
  { kind: "featured-row", label: "Deals & Surplus", icon: Rocket, note: "This week's offers" },
  { kind: "categories", label: "Categories", icon: Sparkle, note: "Shop by category" },
  { kind: "farm-story", label: "Farm story", icon: PencilSimple, note: "About / narrative" },
  { kind: "farm-info", label: "Farm info", icon: Globe, note: "Address + hours" },
  { kind: "market-schedule", label: "Market schedule", icon: Storefront, note: "Where you sell" },
  { kind: "journal", label: "Journal", icon: PencilSimple, note: "Blog posts" },
  { kind: "gallery", label: "Gallery", icon: Camera, note: "Farm photos" },
  { kind: "newsletter", label: "Newsletter", icon: Sparkle, note: "Email signup" },
  { kind: "cta", label: "Call to action", icon: Rocket, note: "Prominent button" },
  { kind: "contact", label: "Contact", icon: ChatCircle, note: "Email / phone / form" },
  { kind: "rich-text", label: "Rich text", icon: PencilSimple, note: "Custom paragraph" },
];

export default function FarmersWebsiteEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, requireSignIn } = useAuth();
  const [state, setState] = useState({ loading: true, error: null, site: null, pages: [] });
  const [themes, setThemes] = useState([]);
  const [activePageId, setActivePageId] = useState(null);
  const [content, setContent] = useState({
    products: [], deals: [], surplus: [], journal: [], media: [], fresh_today: [],
  });
  const [editingBlock, setEditingBlock] = useState(null);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showAddPage, setShowAddPage] = useState(false);
  const [renamingPage, setRenamingPage] = useState(null);
  const [confirmDeletePage, setConfirmDeletePage] = useState(null);
  const [confirmDeleteBlock, setConfirmDeleteBlock] = useState(null);
  const [showLogoStudio, setShowLogoStudio] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const [siteRes, themesRes] = await Promise.all([
        http.get(`/farmers/websites/${id}`),
        http.get(`/farmers/websites/themes`),
      ]);
      setState({
        loading: false, error: null,
        site: siteRes.data.website, pages: siteRes.data.pages || [],
      });
      setThemes(themesRes.data.themes || []);
      setActivePageId((p) => p || (siteRes.data.pages?.[0]?.id));
      // Load resolved content bundle for preview.
      const owner_uid = siteRes.data.website.owner_uid;
      const [prod, deals, surplus, jrn, media] = await Promise.all([
        http.get(`/farmers/shop/products?owner_uid=${owner_uid}&limit=60&published_only=false`).then((r) => r.data.products).catch(() => []),
        http.get(`/farmers/shop/deals?owner_uid=${owner_uid}&limit=30`).then((r) => r.data.deals).catch(() => []),
        http.get(`/farmers/shop/surplus?owner_uid=${owner_uid}&limit=30`).then((r) => r.data.surplus).catch(() => []),
        http.get(`/farmers/shop/journal?owner_uid=${owner_uid}&limit=12&published_only=false`).then((r) => r.data.journal).catch(() => []),
        http.get(`/farmers/shop/media?owner_uid=${owner_uid}&limit=30`).then((r) => r.data.media).catch(() => []),
      ]);
      setContent({ products: prod, deals, surplus, journal: jrn, media, fresh_today: prod.filter((p) => p.fresh_today) });
    } catch (e) {
      setState({ loading: false, error: e?.response?.status === 404 ? "not-found" : "error", site: null, pages: [] });
    }
  }, [id]);

  useEffect(() => { if (user) load(); }, [user, load]);

  const activePage = useMemo(
    () => state.pages.find((p) => p.id === activePageId) || state.pages[0],
    [state.pages, activePageId]
  );

  const themeStyle = useMemo(() => state.site
    ? themeVars(state.site.theme_id, state.site.theme_overrides)
    : {}, [state.site]);

  const patchSite = async (patch) => {
    setBusy(true);
    try {
      const { data } = await http.patch(`/farmers/websites/${id}`, patch);
      setState((s) => ({ ...s, site: data.website }));
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      const { data } = await http.post(`/farmers/websites/${id}/publish`);
      setState((s) => ({ ...s, site: data.website }));
      toast.success("🎉 Your farm site is live!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally { setBusy(false); }
  };

  const addBlock = async (kind) => {
    if (!activePage) return;
    setBusy(true);
    try {
      const { data } = await http.post(`/farmers/pages/${activePage.id}/blocks`, { kind, config: {} });
      setState((s) => ({
        ...s,
        pages: s.pages.map((p) => p.id === activePage.id
          ? { ...p, blocks: [...(p.blocks || []), data.block] }
          : p),
      }));
      setShowAddMenu(false);
      toast.success(`Added ${kind}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add block");
    } finally { setBusy(false); }
  };

  const patchBlock = async (blockId, patch) => {
    if (!activePage) return;
    setBusy(true);
    try {
      const { data } = await http.patch(`/farmers/pages/${activePage.id}/blocks/${blockId}`, patch);
      setState((s) => ({
        ...s,
        pages: s.pages.map((p) => p.id === activePage.id
          ? { ...p, blocks: (p.blocks || []).map((b) => b.id === blockId ? data.block : b) }
          : p),
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update block");
    } finally { setBusy(false); }
  };

  const removeBlock = async (blockId) => {
    if (!activePage) return;
    setBusy(true);
    try {
      await http.delete(`/farmers/pages/${activePage.id}/blocks/${blockId}`);
      setState((s) => ({
        ...s,
        pages: s.pages.map((p) => p.id === activePage.id
          ? { ...p, blocks: (p.blocks || []).filter((b) => b.id !== blockId) }
          : p),
      }));
      toast.success("Removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); setConfirmDeleteBlock(null); }
  };

  const moveBlock = async (blockId, dir) => {
    if (!activePage) return;
    const blocks = [...(activePage.blocks || [])].sort((a, b) => (a.position || 0) - (b.position || 0));
    const idx = blocks.findIndex((b) => b.id === blockId);
    if (idx < 0) return;
    const target = dir === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= blocks.length) return;
    const reordered = blocks.slice();
    [reordered[idx], reordered[target]] = [reordered[target], reordered[idx]];
    try {
      await http.post(`/farmers/pages/${activePage.id}/blocks/reorder`,
        { order: reordered.map((b) => b.id) });
      setState((s) => ({
        ...s,
        pages: s.pages.map((p) => p.id === activePage.id
          ? { ...p, blocks: reordered.map((b, i) => ({ ...b, position: i })) }
          : p),
      }));
    } catch (e) {
      toast.error("Couldn't reorder");
    }
  };

  const toggleHidden = (blockId) => {
    const b = (activePage?.blocks || []).find((x) => x.id === blockId);
    if (!b) return;
    patchBlock(blockId, { hidden: !b.hidden });
  };

  // ----- Pages CRUD ------------------------------------------------
  const addPage = async ({ title, slug }) => {
    setBusy(true);
    try {
      const { data } = await http.post(`/farmers/websites/${id}/pages`,
        { title, slug: slug || title.toLowerCase().replace(/[^a-z0-9]+/g, "-") });
      setState((s) => ({ ...s, pages: [...s.pages, data.page] }));
      setActivePageId(data.page.id);
      setShowAddPage(false);
      toast.success(`Added "${data.page.title}"`);
      // Refresh site to pick up new nav entry
      const r = await http.get(`/farmers/websites/${id}`);
      setState((s) => ({ ...s, site: r.data.website }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create page");
    } finally { setBusy(false); }
  };

  const renamePage = async (pageId, patch) => {
    setBusy(true);
    try {
      const { data } = await http.patch(`/farmers/pages/${pageId}`, patch);
      setState((s) => ({
        ...s,
        pages: s.pages.map((p) => p.id === pageId ? { ...p, ...data.page } : p),
      }));
      setRenamingPage(null);
      toast.success("Page updated");
      const r = await http.get(`/farmers/websites/${id}`);
      setState((s) => ({ ...s, site: r.data.website }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't rename page");
    } finally { setBusy(false); }
  };

  const deletePage = async (pageId) => {
    setBusy(true);
    try {
      await http.delete(`/farmers/pages/${pageId}`);
      setState((s) => {
        const remaining = s.pages.filter((p) => p.id !== pageId);
        return { ...s, pages: remaining };
      });
      if (activePageId === pageId) {
        setActivePageId((state.pages.find((p) => p.id !== pageId) || {}).id);
      }
      setConfirmDeletePage(null);
      toast.success("Page removed");
      const r = await http.get(`/farmers/websites/${id}`);
      setState((s) => ({ ...s, site: r.data.website }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete page");
    } finally { setBusy(false); }
  };

  const setBrandColors = async (overrides) => {
    // overrides: {brand, accent, bg, ...} — merge into theme_overrides
    await patchSite({ theme_overrides: overrides });
  };

  if (!user) {
    return (
      <main className="min-h-screen bg-[#F5EBDC] pt-32 md:pt-36 px-5 md:px-10">
        <FarmersNav />
        <div className="max-w-2xl mx-auto text-center py-20">
          <PencilSimple size={40} weight="duotone" className="mx-auto text-[#B85F2A]" />
          <h1 className="mt-4 font-serif-display text-4xl">Sign in to edit your farm site</h1>
          <button onClick={requireSignIn}
            className="mt-6 rounded-full bg-[#1B4332] text-[#F5EBDC] px-5 py-2.5 text-sm">Sign in</button>
        </div>
      </main>
    );
  }

  if (state.loading) {
    return (
      <main className="min-h-screen bg-[#F5EBDC] pt-32 md:pt-36 px-5 md:px-10">
        <FarmersNav />
        <div className="max-w-2xl mx-auto text-center py-20 text-[#3A3830]">Loading your editor…</div>
      </main>
    );
  }

  if (state.error === "not-found") {
    return (
      <main className="min-h-screen bg-[#F5EBDC] pt-32 md:pt-36 px-5 md:px-10">
        <FarmersNav />
        <div className="max-w-2xl mx-auto text-center py-20">
          <Warning size={30} weight="fill" className="mx-auto text-red-500" />
          <h1 className="mt-4 font-serif-display text-3xl">Site not found</h1>
          <Link to="/farmers/websites" className="mt-4 inline-block text-sm underline">Back to my sites</Link>
        </div>
      </main>
    );
  }

  if (state.error || !state.site) {
    return (
      <main className="min-h-screen bg-[#F5EBDC] pt-32 md:pt-36 px-5 md:px-10" data-testid="editor-error-state">
        <FarmersNav />
        <div className="max-w-2xl mx-auto text-center py-20">
          <Warning size={30} weight="fill" className="mx-auto text-red-500" />
          <h1 className="mt-4 font-serif-display text-3xl">Couldn't load this site</h1>
          <p className="mt-2 text-sm text-[#3A3830]">
            You may not have permission to edit it, or something went wrong on our side.
          </p>
          <div className="mt-6 flex items-center justify-center gap-2">
            <button onClick={() => load()} className="rounded-full bg-[#1B4332] text-[#F5EBDC] px-4 py-2 text-sm" data-testid="editor-error-retry">
              Try again
            </button>
            <Link to="/farmers/websites" className="rounded-full border border-black/15 px-4 py-2 text-sm">Back to my sites</Link>
          </div>
        </div>
      </main>
    );
  }

  const site = state.site;
  const activeBlocks = (activePage?.blocks || []).slice().sort((a, b) => (a.position || 0) - (b.position || 0));

  return (
    <main className="min-h-screen bg-[#F5EBDC] pt-32 md:pt-36 pb-12 px-3 md:px-6" data-testid="farmers-website-editor">
      <FarmersNav />
      <div className="max-w-[1400px] mx-auto">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="min-w-0">
            <Link to="/farmers/websites" className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#1B4332]">
              <ArrowLeft size={11} weight="bold" /> All sites
            </Link>
            <div className="mt-1 flex items-center gap-2">
              <div className="text-lg md:text-2xl font-serif-display truncate">{site.farm_name}</div>
              <span className={`text-[10px] uppercase tracking-[0.22em] font-mono rounded-full px-2 py-0.5 ${
                site.status === "published" ? "bg-emerald-100 text-emerald-800" :
                site.status === "draft" ? "bg-amber-100 text-amber-800" :
                "bg-neutral-100 text-neutral-700"
              }`} data-testid="site-status">{site.status}</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => setShowThemeMenu(true)}
              className="inline-flex items-center gap-1.5 rounded-full border border-black/15 bg-white px-3.5 py-2 text-xs"
              data-testid="editor-change-theme">
              <Palette size={11} weight="fill" /> Theme
            </button>
            <button onClick={() => setShowColorPicker(true)}
              className="inline-flex items-center gap-1.5 rounded-full border border-black/15 bg-white px-3.5 py-2 text-xs"
              data-testid="editor-change-colors">
              <div className="w-3 h-3 rounded-full border border-black/20" style={{ background: site.theme_overrides?.brand || "var(--fs-brand)" }} />
              Colors
            </button>
            <a href={`/f/${site.slug}`} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full border border-black/15 bg-white px-3.5 py-2 text-xs"
              data-testid="editor-preview-link">
              <Eye size={11} weight="fill" /> Preview
            </a>
            <button onClick={() => {
              const url = `${window.location.origin}/f/${site.slug}`;
              const done = () => toast.success("Link copied");
              const fail = () => {
                // Fallback: hidden textarea + execCommand
                try {
                  const ta = document.createElement("textarea");
                  ta.value = url; document.body.appendChild(ta);
                  ta.select(); document.execCommand("copy");
                  document.body.removeChild(ta);
                  done();
                } catch { toast.error("Couldn't copy — copy manually: " + url); }
              };
              if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(url).then(done).catch(fail);
              } else fail();
            }}
              className="inline-flex items-center gap-1.5 rounded-full border border-black/15 bg-white px-3.5 py-2 text-xs"
              data-testid="editor-copy-link">
              <Copy size={11} weight="fill" /> Copy URL
            </button>
            {site.status !== "published" ? (
              <button onClick={publish} disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#B85F2A] text-white px-4 py-2 text-xs font-medium disabled:opacity-50"
                data-testid="editor-publish">
                <Rocket size={11} weight="fill" /> Publish
              </button>
            ) : (
              <button onClick={() => patchSite({ status: "draft" })} disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-full border border-black/15 bg-white px-3.5 py-2 text-xs"
                data-testid="editor-unpublish">
                Unpublish
              </button>
            )}
          </div>
        </div>

        {/* URL strip */}
        <div className="mb-4 rounded-full border border-black/10 bg-white/70 px-4 py-2 inline-flex items-center gap-2 text-xs">
          <Globe size={12} weight="fill" className="text-[#B85F2A]" />
          <span className="text-[#3A3830]">Your public URL:</span>
          <a href={`/f/${site.slug}`} target="_blank" rel="noreferrer" className="font-mono text-[#1B4332] hover:underline">
            /f/{site.slug}
          </a>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[300px,1fr] gap-4">
          {/* LEFT PANEL */}
          <aside className="space-y-4">
            {/* Pages */}
            <div className="rounded-2xl border border-black/10 bg-white p-4">
              <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#3A3830] mb-2 flex items-center justify-between">
                <span>Pages</span>
                <button onClick={() => setShowAddPage(true)}
                  className="rounded-full bg-[#B85F2A] text-white w-5 h-5 flex items-center justify-center hover:scale-110 transition"
                  title="Add page"
                  data-testid="editor-add-page">
                  <Plus size={11} weight="bold" />
                </button>
              </div>
              <div className="flex flex-col gap-1">
                {state.pages.map((p) => {
                  const on = p.id === activePage?.id;
                  return (
                    <div key={p.id} className={`group rounded-lg ${on ? "bg-[#B85F2A]" : "bg-neutral-50 hover:bg-neutral-100"}`}>
                      <button onClick={() => setActivePageId(p.id)}
                        className={`w-full text-left px-3 py-2 text-sm ${on ? "text-white" : ""}`}
                        data-testid={`editor-page-${p.slug}`}>
                        <div className="font-medium">{p.title}</div>
                        <div className={`text-[10px] mt-0.5 ${on ? "text-white/80" : "text-[#3A3830]"}`}>
                          {(p.blocks || []).length} block{(p.blocks || []).length !== 1 ? "s" : ""} · /{p.slug}
                        </div>
                      </button>
                      <div className={`px-2 pb-2 flex items-center gap-1 ${on ? "opacity-100" : "opacity-0 group-hover:opacity-100"} transition`}>
                        <button onClick={() => setRenamingPage(p)}
                          title="Rename"
                          className={`p-1 rounded hover:bg-white/20 ${on ? "text-white" : "text-[#3A3830]"}`}
                          data-testid={`editor-page-rename-${p.slug}`}>
                          <PencilSimple size={11} weight="fill" />
                        </button>
                        {p.slug !== "home" && (
                          <button onClick={() => setConfirmDeletePage(p)}
                            title="Delete"
                            className={`p-1 rounded hover:bg-red-500 hover:text-white ${on ? "text-white" : "text-[#3A3830]"}`}
                            data-testid={`editor-page-delete-${p.slug}`}>
                            <Trash size={11} weight="fill" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Add block */}
            <div className="rounded-2xl border border-black/10 bg-white p-4">
              <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#3A3830] mb-2">Add block</div>
              <div className="grid grid-cols-2 gap-1.5">
                {BLOCK_CATALOG.slice(0, 8).map((b) => (
                  <button key={b.kind} onClick={() => addBlock(b.kind)}
                    className="text-left rounded-lg border border-black/10 hover:border-[#B85F2A] px-2.5 py-2"
                    data-testid={`editor-add-${b.kind}`}>
                    <div className="flex items-center gap-1.5 text-[11px] font-medium">
                      <b.icon size={11} weight="fill" className="text-[#B85F2A]" /> {b.label}
                    </div>
                  </button>
                ))}
              </div>
              <button onClick={() => setShowAddMenu(true)}
                className="mt-2 w-full rounded-lg border border-dashed border-black/20 px-2.5 py-2 text-[11px] hover:border-[#B85F2A]"
                data-testid="editor-add-more">
                + more block types
              </button>
            </div>

            {/* Logo Studio */}
            <div className="rounded-2xl border border-black/10 bg-white p-4 space-y-3" data-testid="editor-logo-panel">
              <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#3A3830] flex items-center justify-between">
                <span>Logo</span>
                <button onClick={() => setShowLogoStudio(true)}
                  className="inline-flex items-center gap-1 rounded-full bg-[#1B4332] text-[#F5EBDC] px-2.5 py-1 text-[10px] font-medium hover:bg-black"
                  data-testid="editor-open-logo-studio">
                  <MagicWand size={10} weight="fill" /> AI logos
                </button>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-2xl border border-black/10 flex items-center justify-center overflow-hidden bg-neutral-50 shrink-0">
                  {site.logo_url ? (
                    <img src={site.logo_url} alt="Logo" className="w-full h-full object-contain" />
                  ) : (
                    <Storefront size={20} weight="duotone" className="text-[#3A3830]" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-[#3A3830]">Logo URL</div>
                  <input defaultValue={site.logo_url || ""}
                    onBlur={(e) => (e.target.value || "") !== (site.logo_url || "") && patchSite({ logo_url: e.target.value || null })}
                    placeholder="Paste a URL or generate with AI"
                    className="mt-1 w-full rounded-lg border border-black/15 px-2.5 py-1.5 text-xs"
                    data-testid="editor-logo-url" />
                </div>
              </div>
              {(site.logo_candidates || []).length > 0 && (
                <div>
                  <div className="text-[10px] text-[#3A3830] mb-1">Saved AI variants ({site.logo_candidates.length})</div>
                  <div className="flex gap-1.5 overflow-x-auto">
                    {site.logo_candidates.slice(0, 8).map((c, i) => (
                      <button key={i}
                        onClick={() => patchSite({ logo_url: c.url })}
                        title={c.style || "logo"}
                        className={`w-12 h-12 shrink-0 rounded-lg border overflow-hidden hover:border-[#B85F2A] ${site.logo_url === c.url ? "border-[#B85F2A] ring-2 ring-[#B85F2A]/40" : "border-black/10"}`}
                        data-testid={`editor-logo-variant-${i}`}>
                        <img src={c.url} alt={c.style} className="w-full h-full object-contain bg-white" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Settings */}
            <div className="rounded-2xl border border-black/10 bg-white p-4 space-y-3">
              <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#3A3830]">Site basics</div>
              <label className="block">
                <div className="text-xs text-[#3A3830]">Farm name</div>
                <input defaultValue={site.farm_name} onBlur={(e) => e.target.value !== site.farm_name && patchSite({ farm_name: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-black/15 px-2.5 py-1.5 text-sm"
                  data-testid="editor-farm-name" />
              </label>
              <label className="block">
                <div className="text-xs text-[#3A3830]">Tagline</div>
                <input defaultValue={site.tagline || ""} onBlur={(e) => (e.target.value || "") !== (site.tagline || "") && patchSite({ tagline: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-black/15 px-2.5 py-1.5 text-sm"
                  data-testid="editor-tagline" />
              </label>
              <label className="block">
                <div className="text-xs text-[#3A3830]">Short description</div>
                <textarea defaultValue={site.short_description || ""} rows={2}
                  onBlur={(e) => (e.target.value || "") !== (site.short_description || "") && patchSite({ short_description: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-black/15 px-2.5 py-1.5 text-sm"
                  data-testid="editor-short-desc" />
              </label>
              <label className="block">
                <div className="text-xs text-[#3A3830]">Hero image URL</div>
                <input defaultValue={site.hero_image_url || ""} onBlur={(e) => (e.target.value || "") !== (site.hero_image_url || "") && patchSite({ hero_image_url: e.target.value })}
                  placeholder="https://…"
                  className="mt-1 w-full rounded-lg border border-black/15 px-2.5 py-1.5 text-xs"
                  data-testid="editor-hero-url" />
              </label>
              <label className="block">
                <div className="text-xs text-[#3A3830]">Contact email</div>
                <input defaultValue={site.contact?.email || ""}
                  onBlur={(e) => (e.target.value || "") !== (site.contact?.email || "") && patchSite({ contact: { ...(site.contact || {}), email: e.target.value } })}
                  placeholder="hello@yourfarm.com"
                  className="mt-1 w-full rounded-lg border border-black/15 px-2.5 py-1.5 text-sm"
                  data-testid="editor-contact-email" />
              </label>
              <label className="block">
                <div className="text-xs text-[#3A3830]">Contact phone</div>
                <input defaultValue={site.contact?.phone || ""}
                  onBlur={(e) => (e.target.value || "") !== (site.contact?.phone || "") && patchSite({ contact: { ...(site.contact || {}), phone: e.target.value } })}
                  placeholder="+1 555 555 5555"
                  className="mt-1 w-full rounded-lg border border-black/15 px-2.5 py-1.5 text-sm"
                  data-testid="editor-contact-phone" />
              </label>
            </div>
          </aside>

          {/* RIGHT PANEL — preview */}
          <section className="rounded-2xl border border-black/10 bg-white overflow-hidden" data-testid="editor-canvas">
            {/* Block outline strip */}
            <div className="border-b border-black/10 px-4 py-2 flex flex-wrap items-center gap-2 bg-neutral-50">
              <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#3A3830]">
                Editing / {activePage?.title || "—"} · {activeBlocks.length} block{activeBlocks.length !== 1 ? "s" : ""}
              </div>
              <div className="ml-auto text-[10px] uppercase tracking-[0.28em] font-mono text-[#3A3830]">
                <Sparkle size={10} weight="fill" className="inline mr-0.5 text-[#B85F2A]" /> WYSIWYG preview
              </div>
            </div>

            {/* Preview shell with theme tokens applied */}
            <div style={{ ...themeStyle, background: "var(--fs-bg)" }}>
              <FarmerSiteHeader site={site} />
              <div>
                {activeBlocks.length === 0 && (
                  <div className="py-20 text-center px-6">
                    <Plus size={30} weight="bold" style={{ color: "var(--fs-brand)" }} className="mx-auto" />
                    <p className="mt-3 text-sm" style={{ color: "var(--fs-muted)" }}>
                      This page has no blocks yet. Add one from the left panel.
                    </p>
                  </div>
                )}
                {activeBlocks.map((b) => (
                  <div key={b.id} className="relative group" data-testid={`editor-block-${b.kind}`}>
                    {/* Overlay controls */}
                    <div className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition flex items-center gap-1 bg-black/85 rounded-full px-1 py-1 text-white">
                      <button onClick={() => setEditingBlock(b)} title="Edit"
                        className="w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center"
                        data-testid={`block-edit-${b.id}`}>
                        <PencilSimple size={12} weight="fill" />
                      </button>
                      <button onClick={() => moveBlock(b.id, "up")} title="Move up"
                        className="w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center"
                        data-testid={`block-up-${b.id}`}>
                        <ArrowUp size={12} weight="bold" />
                      </button>
                      <button onClick={() => moveBlock(b.id, "down")} title="Move down"
                        className="w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center"
                        data-testid={`block-down-${b.id}`}>
                        <ArrowDown size={12} weight="bold" />
                      </button>
                      <button onClick={() => toggleHidden(b.id)} title={b.hidden ? "Show" : "Hide"}
                        className="w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center"
                        data-testid={`block-hide-${b.id}`}>
                        {b.hidden ? <EyeSlash size={12} weight="fill" /> : <Eye size={12} weight="fill" />}
                      </button>
                      <button onClick={() => setConfirmDeleteBlock(b)} title="Delete"
                        className="w-7 h-7 rounded-full hover:bg-red-500 flex items-center justify-center"
                        data-testid={`block-delete-${b.id}`}>
                        <Trash size={12} weight="fill" />
                      </button>
                    </div>
                    {b.hidden ? (
                      <div className="p-8 border-2 border-dashed border-red-300 bg-red-50/50 m-4 rounded-xl">
                        <div className="text-xs text-red-700 font-mono uppercase tracking-[0.28em]">
                          Hidden · {b.kind}
                        </div>
                      </div>
                    ) : (
                      renderBlock({ block: b, site, content })
                    )}
                  </div>
                ))}
              </div>
              <FarmerSiteFooter site={site} />
            </div>
          </section>
        </div>
      </div>

      {/* MODALS */}
      {showAddMenu && (
        <ModalOverlay onClose={() => setShowAddMenu(false)}>
          <div className="p-6">
            <div className="flex items-center justify-between">
              <h3 className="font-serif-display text-2xl">Add a block</h3>
              <button onClick={() => setShowAddMenu(false)} className="p-1"><X size={16} weight="bold" /></button>
            </div>
            <p className="text-sm text-[#3A3830] mt-1">Blocks are content-agnostic — switching themes never touches them.</p>
            <div className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-2">
              {BLOCK_CATALOG.map((b) => (
                <button key={b.kind} onClick={() => addBlock(b.kind)}
                  className="text-left rounded-xl border border-black/10 hover:border-[#B85F2A] p-3"
                  data-testid={`add-menu-${b.kind}`}>
                  <div className="flex items-center gap-1.5 text-sm font-medium">
                    <b.icon size={13} weight="fill" className="text-[#B85F2A]" /> {b.label}
                  </div>
                  <div className="mt-1 text-xs text-[#3A3830]">{b.note}</div>
                </button>
              ))}
            </div>
          </div>
        </ModalOverlay>
      )}

      {showThemeMenu && (
        <ModalOverlay onClose={() => setShowThemeMenu(false)}>
          <div className="p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="font-serif-display text-2xl">Change theme</h3>
              <button onClick={() => setShowThemeMenu(false)} className="p-1"><X size={16} weight="bold" /></button>
            </div>
            <p className="text-sm text-[#3A3830] mt-1">Themes only change how your site looks. Content stays the same.</p>
            <div className="mt-5 grid md:grid-cols-2 gap-3">
              {themes.map((t) => {
                const live = t.status === "live";
                const on = site.theme_id === t.id;
                return (
                  <button key={t.id} disabled={!live}
                    onClick={() => { patchSite({ theme_id: t.id }); setShowThemeMenu(false); }}
                    className={`text-left rounded-2xl border overflow-hidden ${on ? "border-[#B85F2A] ring-2 ring-[#B85F2A]/40" : "border-black/10 hover:border-[#B85F2A]"} ${!live ? "opacity-60 cursor-not-allowed" : ""}`}
                    data-testid={`theme-select-${t.id}`}>
                    <div className="aspect-[16/9] bg-neutral-100 relative">
                      {t.preview ? <img src={t.preview} alt={t.name} className="w-full h-full object-cover" /> : (
                        <div className="w-full h-full flex items-center justify-center text-neutral-300">
                          <Palette size={24} weight="fill" />
                        </div>
                      )}
                      {!live && <div className="absolute top-2 right-2 rounded-full bg-white/90 px-2 py-0.5 text-[10px] uppercase tracking-[0.22em] font-mono">Coming soon</div>}
                    </div>
                    <div className="p-3">
                      <div className="text-sm font-medium">{t.name}</div>
                      <div className="text-xs text-[#3A3830]">{t.eyebrow}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </ModalOverlay>
      )}

      {showAddPage && (
        <AddPageModal onClose={() => setShowAddPage(false)} onSave={addPage} busy={busy} />
      )}

      {renamingPage && (
        <RenamePageModal page={renamingPage} onClose={() => setRenamingPage(null)}
          onSave={(patch) => renamePage(renamingPage.id, patch)} busy={busy} />
      )}

      {confirmDeletePage && (
        <ModalOverlay onClose={() => setConfirmDeletePage(null)}>
          <div className="p-6" data-testid="confirm-delete-page">
            <h3 className="font-serif-display text-2xl">Remove &ldquo;{confirmDeletePage.title}&rdquo;?</h3>
            <p className="mt-2 text-sm text-[#3A3830]">
              This removes the page + all its blocks. Visitors will get a 404 if this page's slug was linked from anywhere.
            </p>
            <div className="mt-6 flex items-center justify-end gap-2">
              <button onClick={() => setConfirmDeletePage(null)}
                className="rounded-full border border-black/15 px-4 py-2 text-sm">Cancel</button>
              <button onClick={() => deletePage(confirmDeletePage.id)} disabled={busy}
                className="rounded-full bg-red-600 text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
                data-testid="confirm-delete-page-confirm">
                Remove page
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {showLogoStudio && (
        <LogoStudioModal site={site} onClose={() => setShowLogoStudio(false)}
          onApply={async (url) => { await patchSite({ logo_url: url }); setShowLogoStudio(false); }}
          onUpdateSite={(next) => setState((s) => ({ ...s, site: next }))} />
      )}

      {showColorPicker && (
        <ColorPickerModal site={site} onClose={() => setShowColorPicker(false)}
          onSave={async (overrides) => { await setBrandColors(overrides); setShowColorPicker(false); }} />
      )}

      {editingBlock && (
        <BlockEditor block={editingBlock} onClose={() => setEditingBlock(null)}
          onSave={async (config) => { await patchBlock(editingBlock.id, { config }); setEditingBlock(null); }} />
      )}

      {confirmDeleteBlock && (
        <ModalOverlay onClose={() => setConfirmDeleteBlock(null)}>
          <div className="p-6" data-testid="confirm-delete-block">
            <h3 className="font-serif-display text-2xl">Remove this block?</h3>
            <p className="mt-2 text-sm text-[#3A3830]">
              This removes the <strong>{confirmDeleteBlock.kind}</strong> block from
              &ldquo;{activePage?.title}&rdquo;. You can add it again from the left panel.
            </p>
            <div className="mt-6 flex items-center justify-end gap-2">
              <button onClick={() => setConfirmDeleteBlock(null)}
                className="rounded-full border border-black/15 px-4 py-2 text-sm">Cancel</button>
              <button onClick={() => removeBlock(confirmDeleteBlock.id)} disabled={busy}
                className="rounded-full bg-red-600 text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
                data-testid="confirm-delete-block-confirm">
                Remove block
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </main>
  );
}

/* ------------------------------------------------------------------ */
/*  Block config editor (modal)                                        */
/* ------------------------------------------------------------------ */
function BlockEditor({ block, onSave, onClose }) {
  const [config, setConfig] = useState(block.config || {});
  const set = (k, v) => setConfig((c) => ({ ...c, [k]: v }));

  const fields = FIELD_MAP[block.kind] || [{ key: "title", label: "Title" }];

  return (
    <ModalOverlay onClose={onClose}>
      <div className="p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-serif-display text-2xl">Edit {block.kind}</h3>
          <button onClick={onClose} className="p-1"><X size={16} weight="bold" /></button>
        </div>
        <div className="mt-5 space-y-3" data-testid="block-editor-form">
          {fields.map((f) => (
            <label key={f.key} className="block">
              <div className="text-xs text-[#3A3830]">{f.label}</div>
              {f.type === "textarea" ? (
                <textarea rows={4}
                  value={config[f.key] || ""}
                  onChange={(e) => set(f.key, e.target.value)}
                  className="mt-1 w-full rounded-lg border border-black/15 px-3 py-2 text-sm"
                  placeholder={f.placeholder}
                  data-testid={`block-field-${f.key}`} />
              ) : f.type === "number" ? (
                <input type="number" min={f.min} max={f.max}
                  value={config[f.key] ?? ""}
                  onChange={(e) => set(f.key, e.target.value ? parseInt(e.target.value, 10) : undefined)}
                  className="mt-1 w-full rounded-lg border border-black/15 px-3 py-2 text-sm"
                  placeholder={f.placeholder}
                  data-testid={`block-field-${f.key}`} />
              ) : f.type === "list" ? (
                <input value={(config[f.key] || []).join(", ")}
                  onChange={(e) => set(f.key, e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                  className="mt-1 w-full rounded-lg border border-black/15 px-3 py-2 text-sm"
                  placeholder={f.placeholder}
                  data-testid={`block-field-${f.key}`} />
              ) : (
                <input value={config[f.key] || ""}
                  onChange={(e) => set(f.key, e.target.value)}
                  className="mt-1 w-full rounded-lg border border-black/15 px-3 py-2 text-sm"
                  placeholder={f.placeholder}
                  data-testid={`block-field-${f.key}`} />
              )}
              {f.hint && <div className="text-[11px] text-[#3A3830] mt-0.5">{f.hint}</div>}
            </label>
          ))}
        </div>
        <div className="mt-6 flex items-center justify-end gap-2">
          <button onClick={onClose} className="rounded-full border border-black/15 px-4 py-2 text-sm">Cancel</button>
          <button onClick={() => onSave(config)}
            className="rounded-full bg-[#B85F2A] text-white px-4 py-2 text-sm font-medium"
            data-testid="block-editor-save">
            Save block
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

const FIELD_MAP = {
  "hero": [
    { key: "headline", label: "Headline", placeholder: "Fresh from our farm to your table" },
    { key: "subhead", label: "Subhead", type: "textarea", placeholder: "One sentence about your farm" },
    { key: "image_url", label: "Hero image URL", placeholder: "https://…" },
    { key: "cta_primary", label: "Primary button label", placeholder: "Shop fresh" },
    { key: "cta_primary_to", label: "Primary button link", placeholder: "#products" },
    { key: "cta_secondary", label: "Secondary button label", placeholder: "Visit our farm" },
    { key: "cta_secondary_to", label: "Secondary button link", placeholder: "#contact" },
  ],
  "trust-badges": [
    { key: "badges", label: "Badges", type: "list", placeholder: "Fresh & Local, Family Farmed, Small Batch, Community First", hint: "comma-separated, up to 4" },
  ],
  "fresh-today": [
    { key: "title", label: "Title", placeholder: "Fresh today" },
    { key: "limit", label: "How many to show", type: "number", min: 1, max: 24, placeholder: "6" },
  ],
  "products": [
    { key: "title", label: "Title", placeholder: "Our produce" },
    { key: "category", label: "Filter category", placeholder: "vegetables" },
    { key: "limit", label: "How many to show", type: "number", min: 1, max: 60, placeholder: "24" },
  ],
  "featured-row": [
    { key: "title", label: "Title", placeholder: "Deals & surplus" },
  ],
  "categories": [
    { key: "title", label: "Title", placeholder: "Shop by category" },
  ],
  "farm-story": [
    { key: "title", label: "Title", placeholder: "Our story" },
    { key: "body", label: "Story", type: "textarea", placeholder: "How your farm started, what makes it special" },
    { key: "image_url", label: "Image URL", placeholder: "https://…" },
    { key: "cta_label", label: "Button label" },
    { key: "cta_to", label: "Button link" },
  ],
  "farm-info": [
    { key: "title", label: "Title", placeholder: "Visit us" },
  ],
  "market-schedule": [
    { key: "title", label: "Title", placeholder: "Find us at the market" },
  ],
  "journal": [
    { key: "title", label: "Title", placeholder: "From the farm journal" },
    { key: "limit", label: "How many to show", type: "number", min: 1, max: 24, placeholder: "6" },
  ],
  "gallery": [
    { key: "title", label: "Title", placeholder: "From the farm" },
    { key: "limit", label: "How many to show", type: "number", min: 1, max: 24, placeholder: "8" },
  ],
  "newsletter": [
    { key: "title", label: "Title", placeholder: "Stay in touch with the farm" },
    { key: "prompt", label: "Prompt", type: "textarea", placeholder: "Get seasonal updates, harvest calendars…" },
  ],
  "cta": [
    { key: "headline", label: "Headline", placeholder: "Come see us at the farm" },
    { key: "subhead", label: "Subhead" },
    { key: "cta_label", label: "Button label", placeholder: "Get directions" },
    { key: "cta_to", label: "Button link", placeholder: "#contact" },
  ],
  "contact": [
    { key: "title", label: "Title", placeholder: "Get in touch" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "whatsapp", label: "WhatsApp number" },
  ],
  "rich-text": [
    { key: "body", label: "Text", type: "textarea" },
  ],
};

function ModalOverlay({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="modal-overlay">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-3xl max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-2xl">
        {children}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Add page modal                                                     */
/* ------------------------------------------------------------------ */
function AddPageModal({ onClose, onSave, busy }) {
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const suggested = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return (
    <ModalOverlay onClose={onClose}>
      <div className="p-6" data-testid="add-page-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-serif-display text-2xl">Add a new page</h3>
          <button onClick={onClose} className="p-1"><X size={16} weight="bold" /></button>
        </div>
        <p className="mt-1 text-sm text-[#3A3830]">
          A page is a container for blocks. Add blocks after you create it.
        </p>
        <div className="mt-5 space-y-3">
          <label className="block">
            <div className="text-xs text-[#3A3830]">Page title</div>
            <input value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="Farm Stay, Weddings, Recipes…"
              className="mt-1 w-full rounded-lg border border-black/15 px-3 py-2 text-sm"
              data-testid="add-page-title" autoFocus />
          </label>
          <label className="block">
            <div className="text-xs text-[#3A3830]">URL slug</div>
            <div className="mt-1 flex items-center gap-1 rounded-lg border border-black/15 px-3 py-2 text-sm bg-neutral-50">
              <span className="text-[#3A3830]">/f/…/</span>
              <input value={slug} onChange={(e) => setSlug(e.target.value)}
                placeholder={suggested || "your-page-slug"}
                className="flex-1 bg-transparent outline-none"
                data-testid="add-page-slug" />
            </div>
          </label>
        </div>
        <div className="mt-6 flex items-center justify-end gap-2">
          <button onClick={onClose} className="rounded-full border border-black/15 px-4 py-2 text-sm">Cancel</button>
          <button onClick={() => onSave({ title: title.trim(), slug: (slug || suggested).trim() })}
            disabled={busy || title.trim().length < 2}
            className="rounded-full bg-[#B85F2A] text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
            data-testid="add-page-save">
            Add page
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

/* ------------------------------------------------------------------ */
/*  Rename page modal                                                  */
/* ------------------------------------------------------------------ */
function RenamePageModal({ page, onClose, onSave, busy }) {
  const [title, setTitle] = useState(page.title || "");
  const [slug, setSlug] = useState(page.slug || "");
  return (
    <ModalOverlay onClose={onClose}>
      <div className="p-6" data-testid="rename-page-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-serif-display text-2xl">Edit page</h3>
          <button onClick={onClose} className="p-1"><X size={16} weight="bold" /></button>
        </div>
        <div className="mt-5 space-y-3">
          <label className="block">
            <div className="text-xs text-[#3A3830]">Page title</div>
            <input value={title} onChange={(e) => setTitle(e.target.value)}
              className="mt-1 w-full rounded-lg border border-black/15 px-3 py-2 text-sm"
              data-testid="rename-page-title" autoFocus />
          </label>
          <label className="block">
            <div className="text-xs text-[#3A3830]">URL slug</div>
            <div className="mt-1 flex items-center gap-1 rounded-lg border border-black/15 px-3 py-2 text-sm bg-neutral-50">
              <span className="text-[#3A3830]">/f/…/</span>
              <input value={slug} onChange={(e) => setSlug(e.target.value)}
                className="flex-1 bg-transparent outline-none"
                data-testid="rename-page-slug" />
            </div>
          </label>
        </div>
        <div className="mt-6 flex items-center justify-end gap-2">
          <button onClick={onClose} className="rounded-full border border-black/15 px-4 py-2 text-sm">Cancel</button>
          <button onClick={() => {
            const patch = {};
            if (title.trim() !== page.title) patch.title = title.trim();
            if (slug.trim() !== page.slug) patch.slug = slug.trim();
            if (Object.keys(patch).length === 0) onClose();
            else onSave(patch);
          }}
            disabled={busy || title.trim().length < 1}
            className="rounded-full bg-[#B85F2A] text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
            data-testid="rename-page-save">
            Save changes
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

/* ------------------------------------------------------------------ */
/*  AI Logo Studio modal                                                */
/* ------------------------------------------------------------------ */
function LogoStudioModal({ site, onClose, onApply, onUpdateSite }) {
  const [styles, setStyles] = useState(["badge", "wordmark", "crest", "modern"]);
  const [accentColor, setAccentColor] = useState(site.theme_overrides?.brand || "");
  const [hint, setHint] = useState("");
  const [generating, setGenerating] = useState(false);
  const [freshLogos, setFreshLogos] = useState([]);
  const candidates = site.logo_candidates || [];

  const toggleStyle = (s) => setStyles((cur) =>
    cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]);

  const generate = async () => {
    if (styles.length === 0) return toast.error("Pick at least one style");
    setGenerating(true);
    setFreshLogos([]);
    try {
      const { data } = await http.post(`/farmers/websites/${site.id}/logos/generate`, {
        styles,
        accent_color: accentColor || null,
        extra_hint: hint || null,
      });
      setFreshLogos(data.logos || []);
      toast.success(`Generated ${data.logos.length} logo${data.logos.length !== 1 ? "s" : ""}`);
      // Refresh site.logo_candidates locally
      onUpdateSite({ ...site, logo_candidates: data.candidates });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't generate logos");
    } finally { setGenerating(false); }
  };

  const gallery = freshLogos.length > 0 ? freshLogos : candidates;

  return (
    <ModalOverlay onClose={onClose}>
      <div className="p-6" data-testid="logo-studio-modal">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#B85F2A] flex items-center gap-1">
              <MagicWand size={11} weight="fill" /> AI Logo Studio
            </div>
            <h3 className="mt-1 font-serif-display text-2xl">Generate a logo for {site.farm_name}</h3>
          </div>
          <button onClick={onClose} className="p-1"><X size={16} weight="bold" /></button>
        </div>

        <div className="mt-5 grid md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#3A3830] mb-2">Styles ({styles.length}/4)</div>
              <div className="grid grid-cols-2 gap-1.5">
                {[
                  { k: "badge",    label: "Rustic badge",    hint: "Vintage circular emblem" },
                  { k: "wordmark", label: "Editorial",       hint: "Serif wordmark + small mark" },
                  { k: "crest",    label: "Heritage crest",  hint: "Shield with wheat/leaves" },
                  { k: "modern",   label: "Modern minimal",  hint: "Clean sans + flat mark" },
                ].map((s) => {
                  const on = styles.includes(s.k);
                  return (
                    <button key={s.k} onClick={() => toggleStyle(s.k)}
                      className={`text-left rounded-xl border p-2.5 ${on ? "border-[#B85F2A] bg-[#FFF3E7]" : "border-black/10 hover:border-[#B85F2A]"}`}
                      data-testid={`logo-style-${s.k}`}>
                      <div className="text-xs font-medium flex items-center gap-1">
                        {on && <Check size={10} weight="bold" className="text-[#B85F2A]" />}
                        {s.label}
                      </div>
                      <div className="text-[10px] text-[#3A3830] mt-0.5">{s.hint}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#3A3830]">Accent color</div>
              <div className="mt-1 flex items-center gap-2">
                <input type="color" value={accentColor || "#B85F2A"}
                  onChange={(e) => setAccentColor(e.target.value)}
                  className="w-9 h-9 rounded-lg border border-black/15 cursor-pointer"
                  data-testid="logo-accent-color" />
                <input value={accentColor} onChange={(e) => setAccentColor(e.target.value)}
                  placeholder="Optional — auto if blank"
                  className="flex-1 rounded-lg border border-black/15 px-2.5 py-1.5 text-xs font-mono" />
              </div>
            </label>

            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#3A3830]">Extra hint</div>
              <textarea value={hint} onChange={(e) => setHint(e.target.value)}
                rows={2} maxLength={200}
                placeholder="Optional — e.g. include a tomato, sun rays, apple tree, hand-drawn feel…"
                className="mt-1 w-full rounded-lg border border-black/15 px-2.5 py-1.5 text-xs"
                data-testid="logo-hint" />
            </label>

            <button onClick={generate} disabled={generating}
              className="w-full rounded-full bg-[#B85F2A] text-white px-4 py-2.5 text-sm font-medium inline-flex items-center gap-1.5 justify-center disabled:opacity-60"
              data-testid="logo-generate">
              {generating ? "Generating…" : (<><MagicWand size={12} weight="fill" /> Generate logos</>)}
            </button>
            <p className="text-[10px] text-[#3A3830] text-center">
              Uses Gemini Nano Banana. Each generation takes 10-30 seconds.
            </p>
          </div>

          <div className="space-y-3">
            <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#3A3830]">
              {freshLogos.length > 0 ? "Just generated" : (candidates.length > 0 ? "Your library" : "Preview")}
            </div>
            {generating && (
              <div className="grid grid-cols-2 gap-2">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="aspect-square bg-neutral-100 animate-pulse rounded-2xl" />
                ))}
              </div>
            )}
            {!generating && gallery.length === 0 && (
              <div className="rounded-2xl border-2 border-dashed border-black/10 p-8 text-center">
                <MagicWand size={30} weight="duotone" className="mx-auto text-[#B85F2A]" />
                <p className="mt-2 text-xs text-[#3A3830]">Pick styles, then generate.</p>
              </div>
            )}
            {!generating && gallery.length > 0 && (
              <div className="grid grid-cols-2 gap-2">
                {gallery.slice(0, 12).map((logo, i) => (
                  <button key={i} onClick={() => onApply(logo.url)}
                    className={`group aspect-square rounded-2xl border overflow-hidden ${site.logo_url === logo.url ? "border-[#B85F2A] ring-2 ring-[#B85F2A]/40" : "border-black/10 hover:border-[#B85F2A]"} relative`}
                    data-testid={`logo-candidate-${i}`}>
                    <img src={logo.url} alt={logo.style} className="w-full h-full object-contain bg-white" />
                    <div className="absolute inset-x-0 bottom-0 bg-black/80 text-white text-[10px] py-1 opacity-0 group-hover:opacity-100 transition text-center">
                      Use this logo
                    </div>
                    {logo.style && (
                      <div className="absolute top-1 left-1 rounded-full bg-white/90 px-1.5 py-0.5 text-[9px] font-mono capitalize">
                        {logo.style}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </ModalOverlay>
  );
}

/* ------------------------------------------------------------------ */
/*  Brand color picker modal                                           */
/* ------------------------------------------------------------------ */
const COLOR_PRESETS = [
  { name: "Sunset",  brand: "#F97316", accent: "#166534" },
  { name: "Forest",  brand: "#166534", accent: "#B45309" },
  { name: "Ocean",   brand: "#1E3D5B", accent: "#3A9AC7" },
  { name: "Emerald", brand: "#2E8B4A", accent: "#F57C00" },
  { name: "Berry",   brand: "#B91C4B", accent: "#166534" },
  { name: "Amber",   brand: "#B45309", accent: "#166534" },
  { name: "Ink",     brand: "#1B4332", accent: "#B85F2A" },
];

function ColorPickerModal({ site, onClose, onSave }) {
  const current = site.theme_overrides || {};
  const [colors, setColors] = useState({
    brand: current.brand || "",
    accent: current.accent || "",
    bg: current.bg || "",
    panel: current.panel || "",
    ink: current.ink || "",
  });
  const set = (k, v) => setColors((c) => ({ ...c, [k]: v }));
  return (
    <ModalOverlay onClose={onClose}>
      <div className="p-6" data-testid="color-picker-modal">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-serif-display text-2xl">Custom colors</h3>
            <p className="mt-1 text-sm text-[#3A3830]">
              Override the theme's palette. Leave blank to use the theme default.
            </p>
          </div>
          <button onClick={onClose} className="p-1"><X size={16} weight="bold" /></button>
        </div>

        <div className="mt-5">
          <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#3A3830] mb-2">Quick presets</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {COLOR_PRESETS.map((p) => (
              <button key={p.name} onClick={() => setColors((c) => ({ ...c, brand: p.brand, accent: p.accent }))}
                className="text-left rounded-xl border border-black/10 hover:border-[#B85F2A] p-2.5"
                data-testid={`color-preset-${p.name.toLowerCase()}`}>
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-4 rounded-full" style={{ background: p.brand }} />
                  <div className="w-4 h-4 rounded-full" style={{ background: p.accent }} />
                </div>
                <div className="mt-1 text-xs font-medium">{p.name}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5 space-y-3">
          <ColorRow label="Brand (buttons, badges, primary)" value={colors.brand}
            onChange={(v) => set("brand", v)} testid="color-brand" />
          <ColorRow label="Accent (secondary highlights)" value={colors.accent}
            onChange={(v) => set("accent", v)} testid="color-accent" />
          <ColorRow label="Background" value={colors.bg}
            onChange={(v) => set("bg", v)} testid="color-bg" />
          <ColorRow label="Panel / card background" value={colors.panel}
            onChange={(v) => set("panel", v)} testid="color-panel" />
          <ColorRow label="Ink (text color)" value={colors.ink}
            onChange={(v) => set("ink", v)} testid="color-ink" />
        </div>

        <div className="mt-6 flex items-center justify-between gap-2">
          <button onClick={() => onSave({})}
            className="rounded-full border border-black/15 px-4 py-2 text-sm"
            data-testid="color-reset">
            Reset to theme
          </button>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="rounded-full border border-black/15 px-4 py-2 text-sm">Cancel</button>
            <button onClick={() => onSave(Object.fromEntries(Object.entries(colors).filter(([, v]) => v)))}
              className="rounded-full bg-[#B85F2A] text-white px-4 py-2 text-sm font-medium"
              data-testid="color-save">
              Apply colors
            </button>
          </div>
        </div>
      </div>
    </ModalOverlay>
  );
}

function ColorRow({ label, value, onChange, testid }) {
  return (
    <label className="flex items-center gap-3">
      <input type="color" value={value || "#000000"}
        onChange={(e) => onChange(e.target.value)}
        className="w-10 h-10 rounded-lg border border-black/15 cursor-pointer"
        data-testid={testid} />
      <div className="flex-1">
        <div className="text-xs text-[#3A3830]">{label}</div>
        <input value={value} onChange={(e) => onChange(e.target.value)}
          placeholder="Theme default"
          className="mt-0.5 w-full rounded-lg border border-black/15 px-2.5 py-1.5 text-xs font-mono" />
      </div>
    </label>
  );
}
